import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  computed,
  effect,
  inject,
  isSignal,
  linkedSignal,
  signal,
} from '@angular/core'
import { ActivatedRoute } from '@angular/router'

import { Store } from '@ngrx/store'
import { AngularSplitModule } from 'angular-split'

import { FLX_MODAL_DATA, FlxModalData } from '@flowx/angular-ui-toolkit'
import { ExpressionTargetType } from '@flowx/designer/modules/ai/conversation/ai-conversation.dictionary'
import { AnalyticsService } from '@flowx/designer/modules/analytics/analytics.service'
import { ScriptingLanguage } from '@flowx/designer/modules/core/dictionary/common.types'
import { ProcessDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import {
  CodeEditorFlxAutocompleteOptions,
  CodeEditorFlxLanguage,
  ModelMarkerWithId,
} from '@flowx/designer/modules/monaco-code-editor/types'
import { UiDesignerExpressionModalActions } from '@flowx/designer/modules/ui-designer/ui-designer.actions'
import { TestModalComponent } from '@flowx/designer/shared/components/test-modal/test-modal.component'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

import { ExpressionCondition, PreviewMode } from '../../constants/expressions.constants'
import { ExpressionEvalService } from '../../services/expression-eval.service'
import {
  RunDataEmitterPayload,
  SaveDataEmitterPayload,
  ScriptingEditComponent,
} from '../scripting-edit/scripting-edit.component'
import { ScriptingInputDataComponent } from '../scripting-input-data/scripting-input-data.component'
import {
  OutcomeError,
  ScriptingOutputComponent,
} from '../scripting-output/scripting-output.component'

export type ExpressionEditorOutput = {
  errors?: ModelMarkerWithId[]
  saveData?: string
  updatedData?: string
  closing?: boolean
  language?: ScriptingLanguage
  useGraalVM?: boolean
}

@Component({
  selector: 'flx-expression-editor',
  imports: [
    AngularSplitModule,
    ScriptingEditComponent,
    ScriptingInputDataComponent,
    ScriptingOutputComponent,
    TestModalComponent,
  ],
  host: { class: 'h-full w-full' },
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './expression-editor.component.html',
})
export class ExpressionEditorModalComponent implements OnInit, OnDestroy {
  #data: FlxModalData = inject(FLX_MODAL_DATA)
  #expressionEvalService = inject(ExpressionEvalService)
  #toastMessageService = inject(ToastMessageService)
  #store = inject(Store)
  #activatedRoute = inject(ActivatedRoute)
  #analyticsService = inject(AnalyticsService)

  @Output() dataEventEmitter = new EventEmitter<ExpressionEditorOutput>()

  readonly autoCompleteOptions: Partial<CodeEditorFlxAutocompleteOptions> = {
    [CodeEditorFlxLanguage.Javascript]: {
      interpolationAutocomplete: true,
      inputObjectAutocomplete: false,
    },
  }

  type = signal<'expression' | 'computed_value'>('expression')
  condition = signal<ExpressionCondition>(null)
  processVersionId = signal<ProcessDefinition['id']>(null)
  processDefinitionName = signal<ProcessDefinition['resourceName']>(null)
  expression = signal<string>('')
  inputString = signal<string>('{}')
  output = signal<any>(undefined)
  outputIsComputed = signal(false)
  error = signal<OutcomeError>(null)
  showOutcome = signal(false)
  readonly = signal(false)
  conversationTargetDescriptor = signal<{
    type: ExpressionTargetType
    index?: number
    nodeId?: number
    propName?: string
    templateId?: number
  }>(null)

  title = linkedSignal(() =>
    this.type() === 'expression' ? `${this.condition()} Expression` : 'Computed Value'
  )
  previewMode = signal<PreviewMode>('none')
  sections = computed(() => ({
    leftSection: {
      title: 'Input Data',
      subtitle: this.processDefinitionName()
        ? 'Choose a process instance or type the input data you want to test'
        : 'Type the input data you want to test',
    },
    middleSection: { title: 'Edit Expression', subtitle: 'Write or build an expression' },
    rightSection: {
      title: 'Expression Output',
      subtitle: 'Result of the input data being processed through the expression',
    },
  }))
  showAiActions = signal(false)
  saveButtonLabel = signal('Save')

  constructor() {
    effect(() => {
      this.dataEventEmitter.emit({ updatedData: this.expression() })
    })

    if (isSignal(this.#data.readonly)) {
      effect(() => {
        const readonlyValue = this.#getReadonlyValue()
        this.readonly.set(readonlyValue)
      })
    }
  }

  onSave(payload: Pick<SaveDataEmitterPayload, 'expression' | 'errors'>): void {
    this.#analyticsService.trackUntyped(
      { object: 'test_expression', action: 'saved' },
      { editor_type: this.type() }
    )
    this.dataEventEmitter.emit({ saveData: payload.expression, errors: payload.errors })
  }

  onRun(payload: RunDataEmitterPayload): void {
    this.error.set(null)
    this.output.set(null)
    this.outputIsComputed.set(false)

    if (this.type() === 'expression') {
      this.showOutcome.set(true)
    }

    let jsonInput = null

    try {
      jsonInput = JSON.parse(this.inputString())
    } catch (e) {
      this.error.set({ msg: "Couldn't parse input" })
    }

    if (!jsonInput) {
      return
    }

    try {
      const evaluatedExpressionOutput =
        this.type() === 'expression'
          ? this.#expressionEvalService.evalLocal(payload.expression, jsonInput)
          : this.#expressionEvalService.evalLocalIsolated(payload.expression, jsonInput)
      this.output.set(evaluatedExpressionOutput)
      this.outputIsComputed.set(true)
    } catch (e) {
      this.error.set({ msg: e })
    } finally {
      this.#analyticsService.trackUntyped(
        { object: 'test_expression', action: 'evaluated' },
        { editor_type: this.type(), result: this.error() ? 'error' : 'success' }
      )
      this.#toastMessageService.showSuccessMessage('Expression evaluated')
    }
  }

  onClose(): void {
    this.#analyticsService.trackUntyped(
      { object: 'test_expression', action: 'closed' },
      { editor_type: this.type() }
    )
    this.dataEventEmitter.emit({ closing: true })
  }

  onPreviewModeChange(mode: PreviewMode): void {
    this.previewMode.set(mode)
  }

  ngOnInit(): void {
    this.type.set(this.#data.type)
    this.#analyticsService.trackUntyped(
      { object: 'test_expression', action: 'opened' },
      { editor_type: this.#data.type }
    )
    this.expression.set(this.#data.expression)
    this.processVersionId.set(this.#data.processVersionId)
    this.processDefinitionName.set(this.#data.processDefinitionName)
    const readonlyValue = this.#getReadonlyValue()
    this.readonly.set(readonlyValue)
    this.showAiActions.set(this.#data.showAiActions)
    this.saveButtonLabel.set(this.#data.saveButtonLabel ?? 'Save')
    if (this.#data.title) {
      this.title.set(this.#data.title)
    }

    if (this.type() === 'expression') {
      this.condition.set(this.#data.condition)
      this.conversationTargetDescriptor.set(this.#data.conversationTargetDescriptor)
    } else {
      const { templateIds, nodeId } = this.#activatedRoute.snapshot.queryParams
      const descriptor = this.#data.conversationTargetDescriptor

      this.conversationTargetDescriptor.set({
        ...descriptor,
        nodeId: descriptor?.nodeId ?? nodeId,
        templateId: descriptor?.templateId ?? templateIds,
      })
    }
  }

  ngOnDestroy(): void {
    this.#store.dispatch(UiDesignerExpressionModalActions.componentDestroy())
  }

  #getReadonlyValue(): boolean {
    const readonlyData = this.#data.readonly
    if (isSignal(readonlyData)) {
      return readonlyData() as boolean
    }

    return readonlyData
  }
}
